import { ChangeDetectorRef, Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { ChatEngineActiveChannelService } from './interfaces/chat-engine/chat-engine-active-channel.service';
import { ChatEngineErrorService, ChatEngineOperations } from './interfaces/chat-engine/chat-engine-error.service';
import { ChatEngineLifeСycleService } from './interfaces/chat-engine/chat-engine-lifecycle-service';
import { ChatEngineSendMessageService } from './interfaces/chat-engine/chat-engine-send-message.service';
import {
  ChatConversation,
  ChatEngineHandle,
  ChatMessage,
  ChatTraceData,
  ChatTypingIndicator,
} from './interfaces/chat-model.interface';
import { ChatPermissions, EditMessageBody } from './interfaces/chat-operations.interface';
import { ChatDialogsService } from './services/chat-dialogs.service';

@Directive()
export abstract class ChatComponent implements OnInit, OnDestroy {
  public static readonly DELAY_MESSAGE_CONSUMED: number = 2000; // 2s

  public chatEngineConnected = false;
  public activeConversation: ChatConversation | null = null;
  public messages: ChatMessage[] = [];
  public currentUserId: string;
  public isAdminSession: boolean;
  public isLoadingMessages = false;
  public memberTypingName: string | null = null;
  public channelExists = false;
  public permissions: ChatPermissions = {};

  protected chatEngineOnSubscriptions$: Subscription[] = [];
  protected otherSubscriptions$: Subscription[] = [];

  protected chatEngineLifeCycleService = inject(ChatEngineLifeСycleService);
  protected chatDialogsService = inject(ChatDialogsService);
  protected chatEngineActiveChannelService = inject(ChatEngineActiveChannelService);
  protected chatEngineSendMessageService = inject(ChatEngineSendMessageService);
  protected chatEngineErrorService = inject(ChatEngineErrorService);
  protected sessionService = inject(SessionService);
  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected changeDetector = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.subscribeOnSessionChanges();
    this.subscribeToEngineReadinessChanges();
  }

  public ngOnDestroy(): void {
    this.changeDetector.detach();
    this.chatEngineActiveChannelService.closeActiveChannel();
    unsubscribeAll(this.chatEngineOnSubscriptions$);
    unsubscribeAll(this.otherSubscriptions$);
  }

  public onRemoveMessage(message: ChatMessage): void {
    this.chatEngineActiveChannelService.removeMessage(message.entity_id);
  }

  public loadMoreMessages(): void {
    this.chatEngineActiveChannelService.loadMoreMessages();
  }

  public onSendTextMessage(message: string): void {
    const channelUniqueName: string = this.activeConversation.channel.channel_external_id;
    this.chatEngineSendMessageService.sendTextMessage(channelUniqueName, message).catch((error: unknown) => {
      this.chatEngineErrorService.onOperationFailed(ChatEngineOperations.SEND_MESSAGE_IN_ACTIVE_CONVERSATION, error, {
        channelUniqueName,
      } as ChatTraceData);
    });
  }

  public onMessageEdited(editMessageBody: EditMessageBody): void {
    // TODO: block sending when engine is not yet ready
    this.chatEngineActiveChannelService.editMessage(editMessageBody.messageId, editMessageBody.messageValue);
  }

  public onUserTyping(): void {
    // TODO: block sending when engine is not yet ready
    this.chatEngineActiveChannelService.currentUserTyping();
  }

  protected onChatEngineReady(): void {
    this.subscribeToConnectionStatusChanges();
    this.subscribeToChannelChanges();
    this.subscribeToTypingChanges();
    this.subscribeToMessages();
    this.subscribeToMessagesLoading();
  }

  protected onChatEngineOff(): void {
    unsubscribeAll(this.chatEngineOnSubscriptions$);
  }

  protected detectChanges(): void {
    safelyDetectChanges(this.changeDetector);
  }

  protected onActiveChannelChanged(activeChannel: ChatConversation | null): void {
    this.channelExists = activeChannel !== null;
    this.memberTypingName = null;
    this.detectChanges();
  }

  protected abstract onConnectionStateChanged(): void;

  protected onSessionChanged(): void {
    this.currentUserId = this.sessionService.userId;
    this.isAdminSession = this.sessionService.isAdminSession();
  }

  private subscribeOnSessionChanges(): void {
    this.onSessionChanged();
    this.otherSubscriptions$.push(this.sessionService.sessionChanges.subscribe(() => this.onSessionChanged()));
  }

  private subscribeToEngineReadinessChanges(): void {
    this.otherSubscriptions$.push(
      this.chatEngineLifeCycleService.engineReadinessChanged.subscribe((engineHandle: ChatEngineHandle) => {
        if (engineHandle.isValid()) {
          this.onChatEngineReady();
        } else {
          this.onChatEngineOff();
        }
      }),
    );
  }

  private subscribeToConnectionStatusChanges(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineLifeCycleService.connectionStatusChanged.subscribe((connected: boolean) => {
        this.chatEngineConnected = connected;
        this.onConnectionStateChanged();
        this.detectChanges();
      }),
    );
  }

  private subscribeToChannelChanges(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineActiveChannelService.activeChannelChanged.subscribe((activeChannel: ChatConversation | null) => {
        this.onActiveChannelChanged(activeChannel);
      }),
    );
  }

  private subscribeToTypingChanges(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineActiveChannelService.typingActivity.subscribe((response: ChatTypingIndicator) => {
        this.memberTypingName = response.isTyping ? response.typingName : null;
        this.detectChanges();
      }),
    );
  }

  private subscribeToMessages(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineActiveChannelService.activeChannelMessagesChanged.subscribe((response: ChatMessage[]) => {
        this.messages = response;
        this.detectChanges();

        // After a delay, schedule marking the loaded messages as read
        if (this.activeConversation) {
          const conversationUniqueNameAtSchedulingPoint: string = this.activeConversation.channel.channel_external_id;

          setTimeout(() => {
            // However, ensure, that we are still in the dialog and reading the same dialog as planned
            if (
              this.activeConversation &&
              this.activeConversation.channel.channel_external_id === conversationUniqueNameAtSchedulingPoint
            ) {
              void this.chatEngineActiveChannelService.markAllMessagesAsConsumed();
            }
          }, ChatComponent.DELAY_MESSAGE_CONSUMED);
        }
      }),
    );
  }

  private subscribeToMessagesLoading(): void {
    this.chatEngineOnSubscriptions$.push(
      this.chatEngineActiveChannelService.loadingMessages.subscribe((isLoadingMessages: boolean) => {
        this.isLoadingMessages = isLoadingMessages;
        this.detectChanges();
      }),
    );
  }
}
